import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Layouts
import MainLayout from '../components/layout/MainLayout';
import PaymentLayoutWrapper from '../components/layout/PaymentLayoutWrapper';
import FortunePaymentWrapper from '../components/layout/FortunePaymentWrapper';

// Pages
import Home from '../pages/Home';
import Login from '../pages/Login';
import Register from '../pages/Register';
import Shop from '../pages/Shop';
import ProductDetail from '../pages/ProductDetail';
import IntegratedCheckout from '../pages/IntegratedCheckout';
import AiFortune from '../pages/AiFortune';
import FortuneRecordDetail from '../pages/FortuneRecordDetail';
import FortuneTeller from '../pages/FortuneTeller';
import FortuneTellerDetail from '../pages/FortuneTellerDetail';
import About from '../pages/About';
import Contact from '../pages/Contact';
import UserPage from '../pages/User';
import Privacy from '../pages/Privacy';
import Terms from '../pages/Terms';
import Collections from '../pages/Collections';
import Crystal from '../pages/Crystal';
import Faq from '../pages/Faq';
import CrystalGuide from '../pages/CrystalGuide';
import FortuneGuide from '../pages/FortuneGuide';
import Blog from '../pages/Blog';
import BlogPost from '../pages/BlogPost';
import PayPalDemo from '../pages/PayPalDemo';
import OrderDetail from '../pages/OrderDetail';
import OrderHistory from '../pages/OrderHistory';
import BookingHistory from '../pages/BookingHistory';
import CheckoutSuccess from '../pages/CheckoutSuccess';
import FortuneBookingCheckout from '../pages/FortuneBookingCheckout';
import Admin from '../components/admin/Admin'; // 修正为正确的大小写
import InitDatabase from '../pages/InitDatabase';
import NotFound from '../pages/NotFound';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) return <div>Loading...</div>;
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();
  
  // 添加调试信息
  console.log("AdminRoute检查:", { 
    user: currentUser ? currentUser.email : "未登录", 
    isAdmin: isAdmin ? "是管理员" : "非管理员", 
    loading 
  });
  
  if (loading) {
    console.log("认证状态加载中...");
    return <div>Loading...</div>;
  }
  
  if (!currentUser) {
    console.log("用户未登录，重定向到登录页");
    return <Navigate to="/login" />;
  }
  
  if (!isAdmin) {
    console.log("用户无管理员权限，重定向到首页");
    return <Navigate to="/" />;
  }
  
  console.log("验证通过，显示管理员界面");
  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Main Layout Routes - Non-payment pages */}
      <Route path="/" element={<MainLayout />}>
        {/* 公开路由 */}
        <Route index element={<Home />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="shop" element={<Shop />} />
        <Route path="product/:id" element={<ProductDetail />} />
        <Route path="ai-fortune" element={<AiFortune />} />
        <Route path="ai-fortune/record/:recordId" element={
          <ProtectedRoute>
            <FortuneRecordDetail />
          </ProtectedRoute>
        } />
        {/* 命理师路由 - 使用独立的支付包装器 */}
        <Route element={<FortunePaymentWrapper />}>
          <Route path="fortune-teller" element={<FortuneTeller />} />
          <Route path="fortune-teller/:id" element={<FortuneTellerDetail />} />
        </Route>
        <Route path="about" element={<About />} />
        <Route path="contact" element={<Contact />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="terms" element={<Terms />} />
        <Route path="collections" element={<Collections />} />
        <Route path="crystal" element={<Crystal />} />
        <Route path="faq" element={<Faq />} />
        <Route path="crystal-guide" element={<CrystalGuide />} />
        <Route path="fortune-guide" element={<FortuneGuide />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:slug" element={<BlogPost />} />

        {/* 用户资料中心（非支付页面） */}
        <Route path="user" element={
          <ProtectedRoute>
            <UserPage />
          </ProtectedRoute>
        } />
        <Route path="user/bookings" element={
          <ProtectedRoute>
            <BookingHistory />
          </ProtectedRoute>
        } />
        <Route path="bookings" element={
          <ProtectedRoute>
            <BookingHistory />
          </ProtectedRoute>
        } />
        
        {/* 数据库初始化 */}
        <Route path="init-database" element={
          <AdminRoute>
            <InitDatabase />
          </AdminRoute>
        } />
        
        {/* 用户资料页面别名 */}
        <Route path="profile" element={
          <ProtectedRoute>
            <UserPage />
          </ProtectedRoute>
        } />
        
        {/* 管理员路由 */}
        <Route path="admin" element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        } />
        <Route path="admin/*" element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        } />

        {/* 所有支付相关页面路由 - 包括购物车和结账 */}
        <Route element={<PaymentLayoutWrapper />}>
          <Route path="cart" element={<IntegratedCheckout />} />
          <Route path="checkout" element={
            <ProtectedRoute>
              <IntegratedCheckout />
            </ProtectedRoute>
          } />
          <Route path="order/:orderId" element={
            <ProtectedRoute>
              <OrderDetail />
            </ProtectedRoute>
          } />
          <Route path="orders" element={
            <ProtectedRoute>
              <OrderHistory />
            </ProtectedRoute>
          } />
          <Route path="order" element={
            <ProtectedRoute>
              <OrderHistory />
            </ProtectedRoute>
          } />
          <Route path="user/orders" element={
            <ProtectedRoute>
              <OrderHistory />
            </ProtectedRoute>
          } />
          <Route path="checkout-success" element={
            <ProtectedRoute>
              <CheckoutSuccess />
            </ProtectedRoute>
          } />
          <Route path="paypal-demo" element={<PayPalDemo />} />
        </Route>
        
        {/* 命理师预约支付路由 - 使用独立的支付包装器 */}
        <Route element={<FortunePaymentWrapper />}>
          <Route path="fortune-booking-checkout" element={
            <ProtectedRoute>
              <FortuneBookingCheckout />
            </ProtectedRoute>
          } />
        </Route>
        
        {/* 404页面 */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;